/* global globalThis */
import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import UserManagement from "../component/pages/Admin/admin/UserManagement";
import OrganizationManagement from "../component/pages/Admin/admin/OrganizationConfiguration";
import ProcessSettings from "../component/pages/Admin/admin/ProcessSettings";
import { Routes, Route, Navigate } from "react-router-dom";
import Header from "../component/header/Header.js";
import Sidebar from "../component/sidebar/Sidebar.js";
import Footer from "../component/footer/Footer.js";
import ErrorPage from "../component/common/ErrorPage.js";
import AccessDenied from "../component/common/AccessDenied.js";
import { LoaderCircle } from "lucide-react";
import axiosClient, { setAutoLogoutReset } from "../api-client/AxiosClient.js";
import { useMsal } from "@azure/msal-react";
import { toast } from "react-toastify";
import useAutoLogout from "../hooks/useAutoLogout";
import AutoLogoutWarning from "../component/common/AutoLogoutWarning/AutoLogoutWarning";

// Permissions granted to an admin user.
const ADMIN_PERMISSIONS = {
  UserMgmt: { canView: true, canAdd: true, canUpdate: true, canDelete: true },
  OrganizationMgmt: {
    canView: true,
    canAdd: true,
    canUpdate: true,
    canDelete: true,
  },
  ObjectConfig: {
    canView: true,
    canAdd: true,
    canUpdate: true,
    canDelete: true,
  },
  TemplateConfig: {
    canView: true,
    canAdd: true,
    canUpdate: true,
    canDelete: true,
  },
  Administration: { canView: true },
};

const Layout = ({ isOpen, setIsOpen }) => {
  const { instance } = useMsal();
  const [accessState, setAccessState] = useState("loading");

  const handleAutoLogout = useCallback(() => {
    instance.logout().catch(() => {});
    toast.warning(
      "Your session has expired due to inactivity. Please login again.",
    );
  }, [instance]);

  const handleManualLogout = useCallback(() => {
    instance.logout().catch(() => {});
  }, [instance]);

  const { showWarning, remainingTime, resetTimer } = useAutoLogout(
    handleAutoLogout,
    120 * 60 * 1000,
    30 * 1000,
  );

  useEffect(() => {
    setAutoLogoutReset(resetTimer);
    return () => { setAutoLogoutReset(null); };
  }, [resetTimer]);

  const handleStayLoggedIn = useCallback(() => { resetTimer(); }, [resetTimer]);

  useEffect(() => {
    const checkAdminAccess = async () => {
      try {
        const response = await axiosClient.get("/api/User/admin-access");
        const data = response?.data;
        const granted =
          data === true ||
          data?.hasAccess === true ||
          data?.adminAccess === true ||
          data?.isAdmin === true;

        if (granted) {
          sessionStorage.setItem(
            "rolesAndPermission",
            JSON.stringify(ADMIN_PERMISSIONS),
          );
          globalThis.dispatchEvent(
            new CustomEvent("permissionsUpdated", { detail: ADMIN_PERMISSIONS }),
          );
          setAccessState("granted");
        } else {
          setAccessState("denied");
        }
      } catch {
        setAccessState("denied");
      }
    };

    checkAdminAccess();
  }, []);

  const getContentStyle = () => ({
    marginLeft: isOpen ? "0px" : "80px",
    padding: "0 10px 10px",
    width: "100%",
    transition: "all 0.3s ease-in-out",
  });

  if (accessState === "loading") {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white bg-opacity-80 z-50">
        <LoaderCircle className="animate-spin text-blue-900" size={50} />
      </div>
    );
  }

  if (accessState === "denied") {
    return <AccessDenied />;
  }

  return (
    <>
      <div
        className="app-wrapper"
        style={{
          minHeight: "100vh",
          boxSizing: "border-box",
          paddingTop: "80px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Header isOpen={isOpen} setIsOpen={setIsOpen} />

        <div className="app-container" style={{ display: "flex", flex: 1 }}>
          <Sidebar isOpen={isOpen} />

          <div className="content" style={getContentStyle()}>
            <Routes>
              <Route
                path="/user-management"
                element={
                  <div className="px-6">
                    <UserManagement />
                  </div>
                }
              />
              <Route
                path="/organization-management"
                element={
                  <div className="px-6">
                    <OrganizationManagement />
                  </div>
                }
              />
              <Route
                path="/process-settings"
                element={
                  <div className="px-6">
                    <ProcessSettings />
                  </div>
                }
              />
              <Route path="/error" element={<ErrorPage />} />
              <Route
                path="*"
                element={<Navigate to="/organization-management" replace />}
              />
            </Routes>
          </div>
        </div>

        <Footer />
      </div>
      <AutoLogoutWarning
        show={showWarning}
        remainingTime={remainingTime}
        onStayLoggedIn={handleStayLoggedIn}
        onLogout={handleManualLogout}
      />
    </>
  );
};

Layout.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
};

export default Layout;
